import React, {useState} from "react";
import _ from "lodash";
import * as Yup from 'yup';
import {useDispatch, useSelector} from "react-redux";
import {Field, Form, Formik, FieldArray} from 'formik';
import {TextField, Select as SelectInput} from 'formik-mui';
import {Card, Grid, FormControl, MenuItem, IconButton} from "@mui/material";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from "@mui/material/Button";
import {selectKYCStatus, submitKYC} from "redux/kyc/kycSlice";
import {enqueueSnackbar} from 'notistack';
import VerifiedIcon from '@mui/icons-material/Verified';
import UploadIcon from '@mui/icons-material/Upload';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {selectCurrentUserProfile} from "redux/userProfile/userProfileSlice";


const Kyc = () => {

  const dispatch = useDispatch();
  const KYCStatus = useSelector(selectKYCStatus);
  const userProfile = useSelector(selectCurrentUserProfile);

  const getValidationSchema = (accountType) => {
    const baseValidation = {
      account_type: Yup.string().required('Account type is required'),
    };

    // Individual account - only validate individual fields
    if (accountType === 'individual') {
      return Yup.object().shape({
        ...baseValidation,
        full_name: Yup.string().trim().required('Full Name is required'),
        email: Yup.string().email('Please enter a valid email address').required('Email is required'),
        // Explicitly allow other fields to be empty/undefined
        company_legal_name: Yup.string().notRequired(),
        corporate_type: Yup.string().notRequired(),
        signatory_name: Yup.string().notRequired(),
        signatory_email: Yup.string().notRequired(),
        joint_applicant_1_name: Yup.string().notRequired(),
        joint_applicant_1_email: Yup.string().notRequired(),
        joint_applicant_2_name: Yup.string().notRequired(),
        joint_applicant_2_email: Yup.string().notRequired(),
        additional_signatories: Yup.array().notRequired()
      });
    }

    // Joint account - only validate joint applicant fields
    if (accountType === 'joint') {
      return Yup.object().shape({
        ...baseValidation,
        joint_applicant_1_name: Yup.string().trim().required('First applicant name is required'),
        joint_applicant_1_email: Yup.string().email('Please enter a valid email address').required('First applicant email is required'),
        joint_applicant_2_name: Yup.string().trim().required('Second applicant name is required'),
        joint_applicant_2_email: Yup.string().email('Please enter a valid email address').required('Second applicant email is required'),
        // Explicitly allow other fields to be empty/undefined
        full_name: Yup.string().notRequired(),
        email: Yup.string().notRequired(),
        company_legal_name: Yup.string().notRequired(),
        corporate_type: Yup.string().notRequired(),
        signatory_name: Yup.string().notRequired(),
        signatory_email: Yup.string().notRequired(),
        additional_signatories: Yup.array().notRequired()
      });
    }

    // Corporate account - only validate corporate fields
    if (accountType === 'corporate') {
      return Yup.object().shape({
        ...baseValidation,
        company_legal_name: Yup.string().trim().required('Company legal name is required'),
        corporate_type: Yup.string().required('Corporate type is required'),
        signatory_name: Yup.string().trim().required('Signatory name is required'),
        signatory_email: Yup.string().email('Please enter a valid email address').required('Signatory email is required'),
        additional_signatories: Yup.array().of(
          Yup.object().shape({
            name: Yup.string().trim().required('Signatory name is required'),
            email: Yup.string().email('Please enter a valid email address').required('Signatory email is required'),
            sequence: Yup.number().required('Sequence order is required')
          })
        ).test('signatories-required', 'All signatory fields must be completed', function(value) {
          if (!value || value.length === 0) return true; // Empty array is allowed
          return value.every(signatory => 
            signatory.name && signatory.name.trim() && 
            signatory.email && signatory.email.trim()
          );
        }),
        // Explicitly allow other fields to be empty/undefined
        full_name: Yup.string().notRequired(),
        email: Yup.string().notRequired(),
        joint_applicant_1_name: Yup.string().notRequired(),
        joint_applicant_1_email: Yup.string().notRequired(),
        joint_applicant_2_name: Yup.string().notRequired(),
        joint_applicant_2_email: Yup.string().notRequired()
      });
    }

    // Default fallback - only require account_type
    return Yup.object().shape(baseValidation);
  };

  const initialValues = {
    account_type: 'individual',
    full_name: `${userProfile.first_name} ${userProfile.last_name}`.trim(),
    email: userProfile.email,
    // Joint account fields
    joint_applicant_1_name: `${userProfile.first_name} ${userProfile.last_name}`.trim(),
    joint_applicant_1_email: userProfile.email,
    joint_applicant_2_name: '',
    joint_applicant_2_email: '',
    // Corporate fields
    company_legal_name: '',
    corporate_type: '',
    signatory_name: '',
    signatory_email: '',
    additional_signatories: [],
    primary_signatory_sequence: 1,
  }

  const handleKYCSubmit = async (values) => {
    await dispatch(submitKYC(values)).unwrap();
    enqueueSnackbar("KYC verification request has been submitted", { variant: 'success' })
  }

  return (
    <Grid container spacing={6}>
      <Grid item xs={12} sm={6}>
        <Card sx={{p: 3}}>
          <Typography color="secondary.dark" fontWeight={500}>KYC Verification</Typography>

          <Formik 
            initialValues={initialValues} 
            onSubmit={handleKYCSubmit}
                        validate={(values) => {
              try {
                getValidationSchema(values.account_type).validateSync(values, { abortEarly: false });
                return {};
              } catch (err) {
                const errors = {};
                err.inner.forEach(error => {
                  errors[error.path] = error.message;
                });
                return errors;
              }
            }}
           >
            {({isSubmitting, errors, touched, values, setFieldValue, resetForm}) => {
              
              // Clear fields when account type changes
              const handleAccountTypeChange = (event) => {
                const newAccountType = event.target.value;
                setFieldValue('account_type', newAccountType);
                
                // Reset all other fields
                const clearedValues = {
                  account_type: newAccountType,
                  // Clear all other fields
                  full_name: '',
                  email: '',
                  joint_applicant_1_name: '',
                  joint_applicant_1_email: '',
                  joint_applicant_2_name: '',
                  joint_applicant_2_email: '',
                  company_legal_name: '',
                  corporate_type: '',
                  signatory_name: '',
                  signatory_email: '',
                  additional_signatories: []
                };
                
                // Reset form with cleared values
                resetForm({ values: clearedValues });
              };

              return (
              <Form >
                {!_.isEmpty(KYCStatus) && (
                  <Box sx={{mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1}}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Current KYC Status: 
                      <Typography
                        component="span"
                        color={KYCStatus.status === "pending" ? "warning.main" : "success.main"}
                        fontWeight={500}
                        sx={{ml: 1}}
                      >
                        {KYCStatus.status.toUpperCase()} {KYCStatus.status === "verified" && <VerifiedIcon fontSize="small" />}
                      </Typography>
                    </Typography>
                  </Box>
                )}

                {/* Account Type Selection */}
                <FormControl fullWidth margin="normal">
                  <Field 
                    component={SelectInput} 
                    id="account_type" 
                    name="account_type" 
                    label="Type of Account"
                    disabled={!_.isEmpty(KYCStatus)}
                    onChange={!_.isEmpty(KYCStatus) ? undefined : handleAccountTypeChange}
                  >
                    <MenuItem value="individual">Individual</MenuItem>
                    <MenuItem value="corporate">Corporate</MenuItem>
                    <MenuItem value="joint">Joint</MenuItem>
                  </Field>
                </FormControl>

                {/* Individual Fields */}
                {values.account_type === 'individual' && (
                  <>
                    <Field 
                      component={TextField} 
                      id="full_name" 
                      name="full_name" 
                      label="Full Name (as stated on Passport or National ID)" 
                      fullWidth 
                      margin="normal" 
                      size="normal" 
                      disabled 
                    />
                    <Field 
                      component={TextField} 
                      id="email" 
                      name="email" 
                      label="Email Address" 
                      margin="normal" 
                      size="normal" 
                      disabled 
                      fullWidth
                    />
                  </>
                )}

                {/* Joint Account Fields */}
                {values.account_type === 'joint' && (
                  <>
                    <Typography variant="h6" sx={{mt: 2, mb: 1}}>First Applicant</Typography>
                    <Field 
                      component={TextField} 
                      id="joint_applicant_1_name" 
                      name="joint_applicant_1_name" 
                      label="Full Name (as stated on Passport or National ID)" 
                      fullWidth 
                      margin="normal" 
                      size="normal" 
                      disabled 
                    />
                    <Field 
                      component={TextField} 
                      id="joint_applicant_1_email" 
                      name="joint_applicant_1_email" 
                      label="Email Address" 
                      margin="normal" 
                      size="normal" 
                      disabled 
                      fullWidth
                    />
                    
                    <Typography variant="h6" sx={{mt: 2, mb: 1}}>Second Applicant</Typography>
                    <Field 
                      component={TextField} 
                      id="joint_applicant_2_name" 
                      name="joint_applicant_2_name" 
                      label="Full Name (as stated on Passport or National ID)" 
                      fullWidth 
                      margin="normal" 
                      size="normal" 
                      disabled={!_.isEmpty(KYCStatus)}
                    />
                    <Field 
                      component={TextField} 
                      id="joint_applicant_2_email" 
                      name="joint_applicant_2_email" 
                      label="Email Address" 
                      margin="normal" 
                      size="normal" 
                      disabled={!_.isEmpty(KYCStatus)}
                      fullWidth
                    />
                  </>
                )}

                {/* Corporate Fields */}
                {values.account_type === 'corporate' && (
                  <>
                    <Field 
                      component={TextField} 
                      id="company_legal_name" 
                      name="company_legal_name" 
                      label="Company Legal Name" 
                      fullWidth 
                      margin="normal" 
                      size="normal"
                      disabled={!_.isEmpty(KYCStatus)}
                    />
                    
                    <FormControl fullWidth margin="normal">
                      <Field 
                        component={SelectInput} 
                        id="corporate_type" 
                        name="corporate_type" 
                        label="Corporate Type"
                        disabled={!_.isEmpty(KYCStatus)}
                      >
                        <MenuItem value="Ltd">Ltd</MenuItem>
                        <MenuItem value="LLP">LLP</MenuItem>
                        <MenuItem value="SSAS">SSAS</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Field>
                    </FormControl>

                    <Typography variant="h6" sx={{mt: 2, mb: 1}}>Primary Signatory</Typography>
                    <Field 
                      component={TextField} 
                      id="signatory_name" 
                      name="signatory_name" 
                      label="Signatory Full Name (as on Passport/ID)" 
                      fullWidth 
                      margin="normal" 
                      size="normal"
                      disabled={!_.isEmpty(KYCStatus)}
                    />
                    <Field 
                      component={TextField} 
                      id="signatory_email" 
                      name="signatory_email" 
                      label="Signatory Email" 
                      fullWidth 
                      margin="normal" 
                      size="normal"
                      disabled={!_.isEmpty(KYCStatus)}
                    />

                    {/* Additional Signatories */}
                    <Typography variant="h6" sx={{mt: 2, mb: 1}}>Additional Signatories</Typography>
                    <FieldArray name="additional_signatories">
                      {({ push, remove }) => (
                        <Box>
                          {values.additional_signatories.map((signatory, index) => (
                            <Box key={index} sx={{border: 1, borderColor: 'grey.300', p: 2, mb: 2, borderRadius: 1}}>
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={5}>
                                  <Field 
                                    component={TextField} 
                                    name={`additional_signatories.${index}.name`} 
                                    label="Full Name" 
                                    fullWidth 
                                    size="small"
                                    disabled={!_.isEmpty(KYCStatus)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={5}>
                                  <Field 
                                    component={TextField} 
                                    name={`additional_signatories.${index}.email`} 
                                    label="Email" 
                                    fullWidth 
                                    size="small"
                                    disabled={!_.isEmpty(KYCStatus)}
                                  />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                  <IconButton 
                                    onClick={() => remove(index)} 
                                    color="error"
                                    disabled={!_.isEmpty(KYCStatus)}
                                  >
                                    <RemoveIcon />
                                  </IconButton>
                                </Grid>
                              </Grid>
                            </Box>
                          ))}
                          {_.isEmpty(KYCStatus) && (
                            <Button 
                              startIcon={<AddIcon />} 
                              onClick={() => push({ name: '', email: '', sequence: values.additional_signatories.length + 2 })}
                              variant="outlined"
                              size="small"
                            >
                              Add Signatory
                            </Button>
                          )}
                        </Box>
                      )}
                    </FieldArray>
                    {errors.additional_signatories && touched.additional_signatories && typeof errors.additional_signatories === 'string' ? (
                      <Typography component="p" fontSize="small" color="error.main" sx={{ pl: 1.5, mt: 1 }}>{errors.additional_signatories}</Typography>
                    ) : null}
                  </>
                )}

                {_.isEmpty(KYCStatus) ? (
                  <Typography align="right">
                      <Button sx={{mt: 2}} type="submit" variant="contained" color="secondary"  endIcon={isSubmitting? <CircularProgress size={20} color="secondary" /> :  <UploadIcon/>} disabled={isSubmitting}> REQUEST KYC VERIFICATION </Button>
                  </Typography>
                ) : (
                  <Typography align="right">
                      <Button sx={{mt: 2}} variant="outlined" disabled> KYC Already Submitted </Button>
                  </Typography>
                )}
              </Form>
              );
            }}
          </Formik>

        </Card>
      </Grid>
      <Grid item xs={12} sm={6}>
          <div>
            <Typography variant="h3" >Due diligence and KYC Verification</Typography>
            <Box sx={{mb: 3, mt: 3}}>
            Please note that your request will be forwarded to our Compliance department. They will process our regulatory Due diligence and KYC verification.<br/>
            This process is essential to verify your identity and maintain the integrity of our services. Subsequently, you will receive an email from our Back Office.<br/>
            This email will contain detailed instructions on how to proceed with the verification process.<br/>
            </Box>
          <ol>
            <li>Follow the instructions in the email to complete your application. This may include submitting personal information and relevant documents as required by our Compliance department.</li>
            <li>Once you submit your information, our Compliance will review your submission. The review process usually takes 24 to 48 hours upon receipt of your documents, after which your account status will be updated.</li>
            <li>Upon successful verification, you will be notified, and your account will be fully activated.</li>
          </ol>
          </div>
      </Grid>
    </Grid>

  );
};

export default Kyc;
